import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { ProductKeys } from '../../../../../common/constants/products';
import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import DateFilterWithCustomRangePicker from '../../../../../common/ui/filters/last-modified-filter';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../controllers/store';
import { filter as lastUpdatedFilter } from '../../../../../controllers/store/search/filters/last-updated';

import { i18n } from './messages';

const LastUpdatedFilter = ({
	testId = 'universal-last-updated-filter',
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
}: {
	testId?: string;
} & FilterProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	// Trello only supports day/week/month/year options
	const trelloExcludedOptions = useMemo(() => ['custom', 'yesterday'], []);
	const optionsToExclude = selectedProduct === ProductKeys.Trello ? trelloExcludedOptions : [];

	const [filterData, { setOption, clearOptions, loadInitialOptions }] = useSelectFilter({
		filterId: lastUpdatedFilter.id,
		optionValuesToExclude: optionsToExclude,
	});

	// This is a workaround to clear the options when the product is change to Trello
	// To avoid Trello query get filtered by un applicable options that might confuse the user
	// If Trello later served as indexed data that support all date options, this can be removed
	useEffect(() => {
		const isTrelloUnapplicableOptionsSelected =
			filterData?.selectedOptions &&
			// @ts-ignore - TS7006 TypeScript 5.9.2 upgrade
			filterData?.selectedOptions.filter((option) => trelloExcludedOptions.includes(option.value))
				.length > 0;
		if (selectedProduct === ProductKeys.Trello && isTrelloUnapplicableOptionsSelected) {
			clearOptions();
		}
	}, [clearOptions, filterData?.selectedOptions, selectedProduct, trelloExcludedOptions]);

	if (!filterData) {
		return null;
	}

	const { options, selectedOptions, customValue } = filterData;

	const label =
		fit === 'compact' && selectedOptions.length > 0 ? '' : formatMessage(i18n.lastUpdated);

	return (
		<DateFilterWithCustomRangePicker
			contentFit={fit}
			options={options}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			label={label}
			onDropdownOpen={loadInitialOptions}
			selectedOptions={selectedOptions}
			customDateRange={customValue || { from: '', to: '' }}
			testId={testId}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default LastUpdatedFilter;
